import React from 'react';
import PropTypes from 'prop-types';
import { Col, Form, Row } from 'react-bootstrap';
import MaskedInput from 'react-text-mask';
import { PHONE_MASK, STATE_OPTIONS, digitsOnly } from './staffFormRules';

/**
 * The "Personal Information" block of the user-creation form: first / last
 * name, mobile, email, address, city, state and zip — same fields, same
 * layout, same field names (fname, lname, mobile, ...) as AddFacilityStaff, so
 * the registration approval modal looks and validates exactly like creating a
 * user by hand (userregistrations.md D9).
 *
 * Controlled: `values` + `onChange(event)` where event.target = { name, value }.
 */
const StaffPersonalFields = ({
  values,
  errors = {},
  onChange,
  onBlur,
  idPrefix = 'staff',
  disabledFields = [],
  emailFeedback = null
}) => {
  const fieldId = name => `${idPrefix}-${name}`;
  const isDisabled = name => disabledFields.includes(name);
  const emit = (name, value) => onChange({ target: { name, value } });

  return (
    <>
      <Row className="gx-2 gy-3 mb-2">
        <Col md="6">
          <Form.Group controlId={fieldId('fname')}>
            <Form.Label className="required">First Name</Form.Label>
            <Form.Control
              placeholder="First Name"
              value={values.fname || ''}
              name="fname"
              onChange={onChange}
              onBlur={onBlur}
              type="text"
              size="sm"
              autoComplete="off"
              disabled={isDisabled('fname')}
              isInvalid={!!errors.fname}
            />
            <Form.Control.Feedback type="invalid">
              {errors.fname}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md="6">
          <Form.Group controlId={fieldId('lname')}>
            <Form.Label className="required">Last Name</Form.Label>
            <Form.Control
              placeholder="Last Name"
              value={values.lname || ''}
              name="lname"
              onChange={onChange}
              onBlur={onBlur}
              type="text"
              size="sm"
              autoComplete="off"
              disabled={isDisabled('lname')}
              isInvalid={!!errors.lname}
            />
            <Form.Control.Feedback type="invalid">
              {errors.lname}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row className="gx-2 gy-3 mb-2">
        <Col md="6">
          <Form.Group controlId={fieldId('mobile')}>
            <Form.Label>Mobile</Form.Label>
            <MaskedInput
              id={fieldId('mobile')}
              mask={PHONE_MASK}
              className={`form-control form-control-sm ${
                errors.mobile ? 'is-invalid' : ''
              }`}
              guide={false}
              placeholder="(XXX) XXX-XXXX"
              name="mobile"
              inputMode="tel"
              value={values.mobile || ''}
              onChange={onChange}
              onBlur={onBlur}
              disabled={isDisabled('mobile')}
            />
            <Form.Control.Feedback type="invalid" className="d-block">
              {errors.mobile}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md="6">
          <Form.Group controlId={fieldId('email')}>
            <Form.Label className="required">Email</Form.Label>
            <Form.Control
              placeholder="Email"
              value={values.email || ''}
              name="email"
              onChange={onChange}
              onBlur={onBlur}
              type="email"
              size="sm"
              autoComplete="off"
              disabled={isDisabled('email')}
              isInvalid={!!errors.email}
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
            {!errors.email && emailFeedback}
          </Form.Group>
        </Col>
      </Row>

      <Row className="gx-2 gy-3 mb-2">
        <Col md="6">
          <Form.Group controlId={fieldId('address')}>
            <Form.Label>Address</Form.Label>
            <Form.Control
              placeholder="Address"
              value={values.address || ''}
              name="address"
              onChange={onChange}
              type="text"
              size="sm"
              disabled={isDisabled('address')}
            />
          </Form.Group>
        </Col>
        <Col md="6">
          <Form.Group controlId={fieldId('address2')}>
            <Form.Label>Address2</Form.Label>
            <Form.Control
              placeholder="Address2"
              value={values.address2 || ''}
              name="address2"
              onChange={onChange}
              type="text"
              size="sm"
              disabled={isDisabled('address2')}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="gx-2 gy-3 mb-2">
        <Col md="4">
          <Form.Group controlId={fieldId('city')}>
            <Form.Label>City</Form.Label>
            <Form.Control
              placeholder="City"
              value={values.city || ''}
              name="city"
              onChange={onChange}
              onBlur={onBlur}
              type="text"
              size="sm"
              disabled={isDisabled('city')}
              isInvalid={!!errors.city}
            />
            <Form.Control.Feedback type="invalid">
              {errors.city}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group controlId={fieldId('state')}>
            <Form.Label>State</Form.Label>
            <Form.Select
              name="state"
              value={values.state || ''}
              onChange={onChange}
              size="sm"
              disabled={isDisabled('state')}
              isInvalid={!!errors.state}
            >
              {STATE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.key}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.state}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md="4">
          <Form.Group controlId={fieldId('zip')}>
            <Form.Label>Zip</Form.Label>
            <Form.Control
              placeholder="Zip"
              value={values.zip || ''}
              name="zip"
              inputMode="numeric"
              maxLength={5}
              onChange={e =>
                emit('zip', digitsOnly(e.target.value).slice(0, 5))
              }
              onBlur={onBlur}
              type="text"
              size="sm"
              disabled={isDisabled('zip')}
              isInvalid={!!errors.zip}
            />
            <Form.Control.Feedback type="invalid">
              {errors.zip}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};

StaffPersonalFields.propTypes = {
  values: PropTypes.shape({
    fname: PropTypes.string,
    lname: PropTypes.string,
    mobile: PropTypes.string,
    email: PropTypes.string,
    address: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string
  }).isRequired,
  errors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  idPrefix: PropTypes.string,
  disabledFields: PropTypes.arrayOf(PropTypes.string),
  emailFeedback: PropTypes.node
};

export default StaffPersonalFields;
